import { useEffect, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import Title from "../../component/title/Title";
import { useAuth } from "../../config/utilities/useAuth";
import Table from "../../component/table/Table";
import { AuditLogsModal } from "../../component/modal/AuditLogsModal";
import { APIGetAuditLogs } from "../../config/utilities/APIs/audit.Api";
import "./AuditLogs.css";

const headers = [
  { key: "module", name: "Module", sortable: false },
  { key: "actionType", name: "Action", sortable: false },
  { key: "fieldsChanged", name: "Details", sortable: false },
  { key: "user", name: "User", sortable: false },
  { key: "time", name: "Timestamp", sortable: false },
];

const actionTypeOptions = [
  { label: "Create", value: "CREATE" },
  { label: "Update", value: "UPDATE" },
  { label: "Delete", value: "DELETE" },
];

const moduleOptions = [
  { label: "Vehicles", value: "vehicles" },
  { label: "Drivers", value: "drivers" },
  { label: "Campaigns", value: "campaigns" },
  { label: "Administrators", value: "admins" },
  { label: "Stops", value: "stops" },
  { label: "Trips", value: "trips" },
  { label: "Parents", value: "parents" },
  { label: "Children", value: "childs" },
  { label: "Schedules", value: "schedules" },
  { label: "Deeplinks", value: "deeplinks" },
  { label: "Contents", value: "contents" },
];

const roleOptions = [
  { label: "Parents", value: "PARENT" },
  { label: "Superadmins", value: "SUPERADMIN" },
  { label: "Admins", value: "ADMIN"},
  { label: "Editors", value: "EDITOR"}
];

export default function AuditLogs() {
  const { privilege } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [fetchedLogData, setFetchedLogData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    actionType: "",
    module: "",
    role: "",
    startDate: "",
    endDate: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showLogsModal, setShowLogsModal] = useState(false);

  const filters = [
    {
      name: "actionType",
      placeholder: "Filter by Action",
      options: actionTypeOptions,
    },
    { name: "module", placeholder: "Filter by Module", options: moduleOptions },
    { name: "role", placeholder: "Filter by Role", options: roleOptions },
  ];

  const fetchLogData = async (
    page = 1,
    filters = selectedFilters,
    searchTerm,
  ) => {
    setLoading(true);

    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ""),
      );
      const normalizedFilters = { ...cleanedFilters };

      // Backward-compatible fallback:
      // if backend expects both dates, convert one-sided filter into an open-ended range.
      if (normalizedFilters.startDate && !normalizedFilters.endDate) {
        normalizedFilters.endDate = "9999-12-31";
      }
      if (!normalizedFilters.startDate && normalizedFilters.endDate) {
        normalizedFilters.startDate = "1970-01-01";
      }

      const response = await APIGetAuditLogs(
        page,
        normalizedFilters,
        setLoading,
        institutionId,
        searchTerm,
      );
      if (response.status === 200) {
        const { data } = response.data;
        setFetchedLogData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogData();
  }, []);

  useEffect(() => {
    fetchLogData(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handleReset = async () => {
    setFetchedLogData([]);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    await fetchLogData(1, selectedFilters, searchTerm);
  };

  const handleOpenLogsModal = (row) => {
    setSelectedRowData(row);
    setShowLogsModal(true);
  };

  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
    setSelectedRowData(null);
  };

  return (
    <>
      <div className="main-page audit-logs-page audit-logs-table-view">
        <NavBar type="audit" action={action} handleReset={handleReset} />
        <div className="content-area audit-logs-content-area">
          <div className="title-container mobile-hide-page-title">
            {" "}
            <Title
              title="Audit Logs"
              subtitle="View logs of all actions on the admin console"
              titleSize="1.5rem"
              subtitleSize="1rem"
              gap="0.5rem"
              lineHeight="2rem"
            />
          </div>
          <div className="main-content-container audit-logs-main-content-container">
            <Table
              data={fetchedLogData?.auditLog || []}
              searchText="Search by user..."
              headers={headers}
              showEmptyHeader={false}
              showFilter={true}
              onClick={handleReset}
              module="audit"
              loading={loading}
              showTableAction={false}
              height="75vh"
              useBackendPagination={true}
              onPageChange={fetchLogData}
              totalPages={fetchedLogData?.pagination?.totalPages || 1}
              currentPage={pageNumber}
              filters={filters}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              totalRecords={fetchedLogData?.pagination?.total || 0}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onRowClick={handleOpenLogsModal}
            />
          </div>
        </div>
      </div>
      <AuditLogsModal
        isOpen={showLogsModal}
        onClose={handleCloseLogsModal}
        selectedData={selectedRowData}
      />
    </>
  );
}
